/**
 * Fiskal Cloud Online Registration (Kassenmeldung) API
 * ## Important notes The API description below offers an initial preview of what the service will look like.  Please be aware that there may still be breaking changes to the API.  When we mention *ELSTER* in our documentation we refer to ELSTER® (**EL**ektronische **ST**euer**ER**klärung).  ## Getting an authentication token  To be able to work with the API you need to get an authentication token to use with all subsequent API calls described below. Get the token via the [Oauth endpoint](https://fiskal.cloud/auth/realms/df/protocol/openid-connect/token) of the Fiskal Cloud. Use client_id=df-api and grant_type=password for requesting a token. > The access token grants same access rights as the user has in the Fiskal Cloud Portal.  Example request to get token via curl: ``` curl -X POST \\ https://fiskal.cloud/auth/realms/df/protocol/openid-connect/token \\ -H \'Accept: application/json\' \\ -H \'Content-Type: application/x-www-form-urlencoded\' \\ -d \'username=<USER_EMAILADDRESS>&password=<USER_PASSWORD>&client_id=df-api&grant_type=password\' ``` Save the access_token entry of the response and pass it as bearer token in all subsequent API calls. Pass it as header parameter. ```\'Authorization: Bearer <ACCESS_TOKEN_FROM_AUTHENTICATION>\'```  For details on access tokens, please check general Oauth documentation like [Oauth.com](https://www.oauth.com/oauth2-servers/access-tokens/access-token-response/)  ## Tenant context All methods of this API work in the context of the logged in user\'s tenant account. For creating new elements you need to set the tenant id in the request. You can retrieve your tenant id by using the /tenants/tenant endpoint. It is fixed, so it can safely stored for later use.  ## Managed Tenant You can use a different tenant for operations if you have a permission to managed them. You can select managed tenant by sending `X-Managed-Tenant` header with `tenantId` of the managed tenant. All operations (creating, updating and getting) are done in the context of the managed tenant with this header.  ``` curl -X GET \\ -i \'https://fiskal.cloud/api/eric/business-premises/{businessPremisesId}/registration-protocols/pdf\' \\ -H \'Authorization: Bearer {{AccessToken}}\' \\ -H \'X-Managed-Tenant: {{managedTenantId}}\' ``` 
 *
 * The version of the OpenAPI document: 0.1.0-preview
 * Contact: info@deutsche-fiskal.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
import { HttpHeaders }                                       from '@angular/common/http';

import { Observable }                                        from 'rxjs';

import { FormTypeEnumDTO } from '../model/models';
import { RegistrationProtocolBodyDTO } from '../model/models';
import { RegistrationProtocolDTO } from '../model/models';
import { RegistrationProtocolResponseDTO } from '../model/models';
import { RegistrationProtocolStatusEnumDTO } from '../model/models';
import { RegistrationProtocolStatusUpdateDTO } from '../model/models';


import { Configuration }                                     from '../configuration';


export interface CreateRegistrationProtocolRequestParams {
    businessPremisesId: string;
    registrationProtocolBodyDTO: RegistrationProtocolBodyDTO;
}

export class ClsCreateRegistrationProtocolRequestParams {
    businessPremisesId: string = undefined;
    registrationProtocolBodyDTO: RegistrationProtocolBodyDTO = undefined;
}

export interface DownloadFormSubmissionPdfRequestParams {
    registrationProtocolId: string;
}

export class ClsDownloadFormSubmissionPdfRequestParams {
    registrationProtocolId: string = undefined;
}

export interface GetBusinessPremisesRegistrationProtocolsRequestParams {
    businessPremisesId: string;
    status?: Array<RegistrationProtocolStatusEnumDTO>;
    isDownloaded?: boolean;
    pageNumber?: number;
    pageSize?: number;
    sort?: string;
}

export class ClsGetBusinessPremisesRegistrationProtocolsRequestParams {
    businessPremisesId: string = undefined;
    status?: Array<RegistrationProtocolStatusEnumDTO> = undefined;
    isDownloaded?: boolean = undefined;
    pageNumber?: number = undefined;
    pageSize?: number = undefined;
    sort?: string = undefined;
}

export interface GetRegistrationProtocolRequestParams {
    registrationProtocolId: string;
}

export class ClsGetRegistrationProtocolRequestParams {
    registrationProtocolId: string = undefined;
}

export interface GetRegistrationProtocolsRequestParams {
    businessPremisesIds?: Array<string>;
    organizationIds?: Array<string>;
    formType?: FormTypeEnumDTO;
    status?: Array<RegistrationProtocolStatusEnumDTO>;
    isDownloaded?: boolean;
    pageNumber?: number;
    pageSize?: number;
    sort?: string;
}

export class ClsGetRegistrationProtocolsRequestParams {
    businessPremisesIds?: Array<string> = undefined;
    organizationIds?: Array<string> = undefined;
    formType?: FormTypeEnumDTO = undefined;
    status?: Array<RegistrationProtocolStatusEnumDTO> = undefined;
    isDownloaded?: boolean = undefined;
    pageNumber?: number = undefined;
    pageSize?: number = undefined;
    sort?: string = undefined;
}

export interface UpdateRegistrationProtocolStatusRequestParams {
    registrationProtocolId: string;
    ifMatch: string;
    registrationProtocolStatusUpdateDTO: RegistrationProtocolStatusUpdateDTO;
}

export class ClsUpdateRegistrationProtocolStatusRequestParams {
    registrationProtocolId: string = undefined;
    ifMatch: string = undefined;
    registrationProtocolStatusUpdateDTO: RegistrationProtocolStatusUpdateDTO = undefined;
}


export interface RegistrationProtocolsApiClientInterface {
    defaultHeaders: HttpHeaders;
    configuration: Configuration;

    /**
     * Trigger registration protocol submission to Elster
     * Manually trigger registration protocol submission to Elster. Available submission types:   * Abgabenordnung (AO) §146a 
* @param requestParameters
     */
    createRegistrationProtocol(requestParameters: CreateRegistrationProtocolRequestParams, extraHttpRequestParams?: any): Observable<RegistrationProtocolDTO>;

    /**
     * Get the generated PDF response file for the business premises from Elster.
     * Download the generated PDF response file. The endpoint returns two types of PDF file:   1. Preview - PDF file with watermark. It is not submission report. Only for checking whether the submission report contains the correct data.   2. Submission - Form submission. Must be downloaded before registration protocol acknowledgement.  The PDF type is downloaded based on the current registration protocol status. 
* @param requestParameters
     */
    downloadFormSubmissionPdf(requestParameters: DownloadFormSubmissionPdfRequestParams, extraHttpRequestParams?: any): Observable<Blob>;

    /**
     * Get list of registration protocols from Elster for the business premises.
     * Get list of registration protocols created during form submission for the business premises. If no form type is specified, form type \&#39;AO146a\&#39; is used as default. 
* @param requestParameters
     */
    getBusinessPremisesRegistrationProtocols(requestParameters: GetBusinessPremisesRegistrationProtocolsRequestParams, extraHttpRequestParams?: any): Observable<RegistrationProtocolResponseDTO>;

    /**
     * Get registration protocol for the business premises from Elster.
     * Get registration protocol created during form submission for the business premises. 
* @param requestParameters
     */
    getRegistrationProtocol(requestParameters: GetRegistrationProtocolRequestParams, extraHttpRequestParams?: any): Observable<RegistrationProtocolDTO>;

    /**
     * Get list of registration protocols from Elster for the tenant.
     * Get list of registration protocols created during form submission for the business premises. If no form type is specified, form type \&#39;AO146a\&#39; is used as default. 
* @param requestParameters
     */
    getRegistrationProtocols(requestParameters: GetRegistrationProtocolsRequestParams, extraHttpRequestParams?: any): Observable<RegistrationProtocolResponseDTO>;

    /**
     * Confirm or acknowledge registration protocol for the business premises for Elster.
     * Confirm or acknowledge registration protocol created during form submission for the business premises.
* @param requestParameters
     */
    updateRegistrationProtocolStatus(requestParameters: UpdateRegistrationProtocolStatusRequestParams, extraHttpRequestParams?: any): Observable<RegistrationProtocolDTO>;

}
